import { Injectable, InjectionToken, inject } from '@angular/core';
import { TRACE_CONFIG, TraceEnvironmentConfig } from './trace.config';

/** Environment trace settings, e.g. { provide: TRACE_ENV_CONFIG, useValue: environment.trace }. */
export const TRACE_ENV_CONFIG = new InjectionToken<TraceEnvironmentConfig | null>('TRACE_ENV_CONFIG', {
  providedIn: 'root',
  factory: () => null,
});

export interface TraceContext {
  traceId: string;
  spanId: string;
  traceparent: string;
}

/**
 * Single source of trace ids. Reused by RUM so telemetry and
 * API calls share the same trace id.
 */
@Injectable({ providedIn: 'root' })
export class TraceService {
  private readonly features = inject<Record<string, boolean>>('features' as never, {
    optional: true,
  });

  private readonly config = inject(TRACE_ENV_CONFIG);

  private readonly errorReferences = new WeakMap<object, string>();

  /** Fraction of new traces marked sampled; invalid config falls back to the default. */
  get sampleRate(): number {
    const rate = this.config?.sampleRate;
    return typeof rate === 'number' && rate >= 0 && rate <= 1 ? rate : TRACE_CONFIG.defaultSampleRate;
  }

  isEnabled(): boolean {
    return this.features?.[TRACE_CONFIG.featureFlag] === true;
  }

  createContext(): TraceContext {
    const traceId = this.randomHex(TRACE_CONFIG.traceIdBytes);
    const spanId = this.randomHex(TRACE_CONFIG.spanIdBytes);
    // The browser is the trace root, so its decision drives server-side (parent-based) sampling.
    // Unsampled requests are still recorded server-side, so the reference stays diagnosable.
    const flags = this.shouldSample() ? TRACE_CONFIG.sampledFlags : TRACE_CONFIG.unsampledFlags;
    return {
      traceId,
      spanId,
      traceparent: `${TRACE_CONFIG.version}-${traceId}-${spanId}-${flags}`,
    };
  }

  /** First 8 hex characters, upper case (ADR §8), for backend X-Trace-Id values and local ids alike. */
  toReference(traceId: string): string {
    return traceId.trim().slice(0, TRACE_CONFIG.referenceLength).toUpperCase();
  }

  private shouldSample(): boolean {
    const rate = this.sampleRate;
    return rate >= 1 || (rate > 0 && Math.random() < rate);
  }

  /** Remembers the reference for a failed request so the ErrorHandler can show it. */
  recordErrorReference(error: object, reference: string): void {
    this.errorReferences.set(error, reference);
  }

  getErrorReference(error: unknown): string | undefined {
    return typeof error === 'object' && error !== null
      ? this.errorReferences.get(error)
      : undefined;
  }

  /** Random lowercase hex of the given byte length; never all zeros. */
  private randomHex(byteLength: number): string {
    const bytes = new Uint8Array(byteLength);
    do {
      crypto.getRandomValues(bytes);
    } while (bytes.every((byte) => byte === 0));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
}
